import { useEffect, useEffectEvent, useMemo, useRef, useState, type ReactNode } from "react"
import { Link } from "@tanstack/react-router"
import { ArrowCounterClockwise, CheckCircle, Columns, Gear, XCircle } from "@phosphor-icons/react"

import { Badge, Button, Checkbox, IconButton } from "~/components/design-system/components"
import {
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "~/components/reusable/Dialog"
import InfoTooltip from "~/components/reusable/InfoTooltip"
import { Tooltip } from "~/components/reusable/Tooltip"
import { ShortcutTooltip } from "~/components/reusable/ShortcutTooltip"
import { VIEW_SHORTCUTS } from "~/constants/hotkeys"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/reusable/Accordion"
import SearchField from "~/components/planning/SearchField"
import { useDisabledColumnIdSet } from "~/hooks/useDisabledColumns"
import { trackEvent } from "~/hooks/useTrackingService"

import {
  buildChooserGroups,
  diffColumnIds,
  EXTERNAL_GROUPS,
  getExtraDataSource,
  type ChooserColumn,
  type RankedColumn,
} from "./columnChooserModel"
import { useColumnChooserV2 } from "./useColumnChooserV2"
import { isShortcutTarget, useOpenColumnChooserStore } from "./openColumnChooser"

type Props = {
  /** The columns to list, labels resolved (see `buildChooserColumns`). */
  columns: readonly ChooserColumn[]
  /** Everything "Select All" may turn on, before the company's disabled columns are removed. */
  columnIds: readonly string[]
  displayedColumnIds: string[]
  setDisplayedColumnIds: (ids: string[]) => void
  /** Shows "Reset to default" when set. */
  defaultColumnIds?: readonly string[]
  /** Called when the dialog closes on a selection that started from "Reset to default". */
  onResetToDefaultApplied?: () => void
  getGroupIcon: (group: string) => ReactNode
  initialExpandedGroups?: string[]
  /** Prefixes the per-table event names ("PO" → "PO Column Shown"); empty for product tables. */
  trackingPrefix?: string
  /** Sent with every event as "Table Id", so one chart can split by table. */
  tableId: string
  /** Text before the count on the trigger ("Columns" → "Columns: 12"); count only when unset. */
  triggerLabel?: string
  triggerTestId?: string
  title?: string
}

type ColumnListProps = {
  columns: RankedColumn[]
  selectedIds: ReadonlySet<string>
  onToggle: (columnId: string) => void
}

function ColumnList({ columns, selectedIds, onToggle }: ColumnListProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 ml-0.5">
      {columns.length > 0 ? (
        columns.map((column) => {
          const extraDataSource = getExtraDataSource(column.id)
          return (
            <label
              key={column.id}
              data-column-id={column.id}
              className="flex items-center gap-1 cursor-pointer transition-colors rounded px-2 py-1.5 -mx-2"
            >
              <Checkbox
                checked={selectedIds.has(column.id)}
                onCheckedChange={() => onToggle(column.id)}
                className="mr-1"
              />
              {extraDataSource ? (
                <Tooltip title={extraDataSource.tooltip}>
                  <div className="text-balance flex items-center gap-1">
                    <span className="capitalize">{column.displayName}</span>
                    <Badge className="ml-1" variant="external">
                      External
                    </Badge>
                  </div>
                </Tooltip>
              ) : (
                <>
                  <span className="first-letter:uppercase">{column.displayName}</span>
                  {column.description && <InfoTooltip body={column.description} />}
                </>
              )}
            </label>
          )
        })
      ) : (
        <div className="text-sm text-fg-muted">No columns found</div>
      )}
    </div>
  )
}

function GroupToggleLabel({ allSelected }: { allSelected: boolean }) {
  return allSelected ? (
    <>
      <XCircle />
      Deselect all
    </>
  ) : (
    <>
      <CheckCircle />
      Select all
    </>
  )
}

type ChooserBodyProps = {
  columns: readonly ChooserColumn[]
  selectedIds: readonly string[]
  searchText: string
  onSearchTextChange: (text: string) => void
  onSearchBlur: (text: string) => void
  userExpandedGroups: string[]
  onExpandedGroupsChange: (groups: string[]) => void
  getGroupIcon: (group: string) => ReactNode
  title: string
  showResetToDefault: boolean
  onToggle: (columnId: string) => void
  onGroupToggle: (groupColumnIds: string[]) => void
  onSelectAll: () => void
  onDeselectAll: () => void
  onResetToDefault: () => void
  onSettingsLinkClick: () => void
  /** `columnChooserV2`: open with the search box focused instead of the first focusable element */
  focusSearchOnOpen: boolean
}

/** The dialog's contents. Mounted only while it is open, so search ranking never runs closed. */
function ChooserBody({
  columns,
  selectedIds,
  searchText,
  onSearchTextChange,
  onSearchBlur,
  userExpandedGroups,
  onExpandedGroupsChange,
  getGroupIcon,
  title,
  showResetToDefault,
  onToggle,
  onGroupToggle,
  onSelectAll,
  onDeselectAll,
  onResetToDefault,
  onSettingsLinkClick,
  focusSearchOnOpen,
}: ChooserBodyProps) {
  const visibleGroups = useMemo(
    () => buildChooserGroups(columns, searchText),
    [columns, searchText],
  )
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const expandedGroups = searchText ? visibleGroups.map(({ group }) => group) : userExpandedGroups
  const searchResultCount = searchText
    ? visibleGroups.reduce((count, group) => count + group.columns.length, 0)
    : null

  return (
    <DialogContent
      className="flex flex-col max-h-[90vh] max-w-4xl gap-0"
      onOpenAutoFocus={
        focusSearchOnOpen
          ? (event) => {
              event.preventDefault()
              if (event.currentTarget instanceof HTMLElement) {
                event.currentTarget.querySelector<HTMLInputElement>('input[type="text"]')?.focus()
              }
            }
          : undefined
      }
    >
      <div className="sticky top-0 bg-base z-20 space-y-4 pb-4 border-b border-base">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <SearchField
              initialSearchText={searchText}
              onSearchTextChange={onSearchTextChange}
              onBlur={onSearchBlur}
              placeholder="Search columns..."
            />
            {searchResultCount !== null && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-fg-subtle">
                {searchResultCount} found
              </div>
            )}
          </div>
          <Button variant="transparent" size="sm" onClick={onSelectAll}>
            <CheckCircle />
            Select All
          </Button>
          <Button variant="transparent" size="sm" onClick={onDeselectAll}>
            <XCircle />
            Clear
          </Button>
          {showResetToDefault && (
            <Button variant="transparent" size="sm" onClick={onResetToDefault}>
              <ArrowCounterClockwise />
              Reset to default
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <Accordion
          type="multiple"
          value={expandedGroups}
          onValueChange={onExpandedGroupsChange}
          className="w-full"
        >
          {visibleGroups.map(({ group, columns: groupColumns }) => {
            const groupColumnIds = groupColumns.map((c) => c.id)
            const selectedCount = groupColumnIds.filter((id) => selectedSet.has(id)).length

            return (
              <AccordionItem key={group} value={group}>
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center justify-between w-full pr-3">
                    <div className="flex items-center gap-2">
                      {getGroupIcon(group)}
                      <span>{group}</span>
                      <Badge variant="neutral" size="sm">
                        {selectedCount}/{groupColumnIds.length}
                      </Badge>
                      {EXTERNAL_GROUPS.has(group) && (
                        <IconButton
                          asChild
                          variant="neutral"
                          size="xs"
                          onClick={onSettingsLinkClick}
                        >
                          <Link to="/settings/custom-data">
                            <Gear />
                          </Link>
                        </IconButton>
                      )}
                    </div>
                    <Button
                      variant="transparent"
                      size="xs"
                      onClick={(e) => {
                        e.stopPropagation()
                        onGroupToggle(groupColumnIds)
                      }}
                    >
                      <GroupToggleLabel allSelected={selectedCount === groupColumnIds.length} />
                    </Button>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <ColumnList
                    columns={groupColumns}
                    selectedIds={selectedSet}
                    onToggle={onToggle}
                  />
                </AccordionContent>
              </AccordionItem>
            )
          })}
        </Accordion>
      </div>
    </DialogContent>
  )
}

/**
 * The column chooser dialog every table shares: grouped checkboxes, ranked search, bulk actions.
 * Edits stay local until the dialog closes, then apply in one `setDisplayedColumnIds` call.
 */
export default function ColumnChooserCore({
  columns,
  columnIds,
  displayedColumnIds,
  setDisplayedColumnIds,
  defaultColumnIds,
  onResetToDefaultApplied,
  getGroupIcon,
  initialExpandedGroups = [],
  trackingPrefix = "",
  tableId,
  triggerLabel,
  triggerTestId,
  title = "Customize Columns",
}: Props) {
  const [searchText, setSearchText] = useState("")
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [localDisplayedColumnIds, setLocalDisplayedColumnIds] = useState<string[]>([])
  const [resetPending, setResetPending] = useState(false)
  const [userExpandedGroups, setUserExpandedGroups] = useState(initialExpandedGroups)
  const disabledColumnIds = useDisabledColumnIdSet()
  const isV2 = useColumnChooserV2()

  const openedAtRef = useRef(0)
  const loggedSearchRef = useRef("")

  const track = (name: string, properties: Record<string, unknown>, prefixed = true) =>
    trackEvent(prefixed && trackingPrefix ? `${trackingPrefix} ${name}` : name, {
      ...properties,
      "Table Id": tableId,
    })

  const logSearch = (text: string, trigger: "Blur" | "Pick" | "Close") => {
    const trimmed = text.trim()
    if (!trimmed || trimmed === loggedSearchRef.current) return
    loggedSearchRef.current = trimmed
    track("Column Search", { "Search Text": trimmed, Trigger: trigger })
  }

  const open = () => {
    openedAtRef.current = Date.now()
    // The search text survives a close; only log it again once it changes.
    loggedSearchRef.current = searchText.trim()
    setLocalDisplayedColumnIds(displayedColumnIds)
    setResetPending(false)
    setIsDialogOpen(true)
    track(
      "Column Chooser Opened",
      { "Shown Count": displayedColumnIds.length, "Available Count": columns.length },
      false,
    )
  }

  const close = (apply: boolean) => {
    const changes = apply
      ? diffColumnIds(displayedColumnIds, localDisplayedColumnIds)
      : { added: [], removed: [] }
    if (apply && resetPending) onResetToDefaultApplied?.()
    if (changes.added.length > 0 || changes.removed.length > 0) {
      setDisplayedColumnIds(localDisplayedColumnIds)
    }
    logSearch(searchText, "Close")
    track(
      "Column Chooser Closed",
      {
        "Columns Added": changes.added,
        "Columns Removed": changes.removed,
        "Added Count": changes.added.length,
        "Removed Count": changes.removed.length,
        "Ms Open": Date.now() - openedAtRef.current,
      },
      false,
    )
    setIsDialogOpen(false)
  }

  const isRequested = useOpenColumnChooserStore((s) => s.requestedTableId === tableId)
  const showShortcut = useOpenColumnChooserStore((s) => isShortcutTarget(s, tableId))
  const clearRequest = useOpenColumnChooserStore((s) => s.clear)
  const openOnRequest = useEffectEvent(() => {
    clearRequest()
    if (!isDialogOpen) open()
  })
  useEffect(() => {
    if (isRequested) openOnRequest()
  }, [isRequested])

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (nextOpen) open()
    else if (isDialogOpen) close(true)
  }

  const handleColumnToggle = (columnId: string) => {
    logSearch(searchText, "Pick")
    if (localDisplayedColumnIds.includes(columnId)) {
      track("Column Hidden", { Column: columnId })
      setLocalDisplayedColumnIds(localDisplayedColumnIds.filter((id) => id !== columnId))
    } else {
      track("Column Shown", { Column: columnId })
      setLocalDisplayedColumnIds([...localDisplayedColumnIds, columnId])
    }
  }

  const handleSelectAll = () => {
    track("Column Chooser Bulk Action", { Action: "Select All" })
    setLocalDisplayedColumnIds(columnIds.filter((id) => !disabledColumnIds.has(id)))
  }

  const handleDeselectAll = () => {
    track("Column Chooser Bulk Action", { Action: "Deselect All" })
    setLocalDisplayedColumnIds([])
  }

  const handleResetToDefault = () => {
    track("Column Chooser Bulk Action", { Action: "Reset to Default" })
    setLocalDisplayedColumnIds((defaultColumnIds ?? []).filter((id) => !disabledColumnIds.has(id)))
    setResetPending(true)
  }

  const handleGroupToggle = (groupColumnIds: string[]) => {
    const selected = new Set(localDisplayedColumnIds)
    if (groupColumnIds.every((id) => selected.has(id))) {
      track("Column Chooser Group Action", { Action: "Deselect Group" })
      const group = new Set(groupColumnIds)
      setLocalDisplayedColumnIds(localDisplayedColumnIds.filter((id) => !group.has(id)))
    } else {
      track("Column Chooser Group Action", { Action: "Select Group" })
      setLocalDisplayedColumnIds([
        ...localDisplayedColumnIds,
        ...groupColumnIds.filter((id) => !selected.has(id)),
      ])
    }
  }

  return (
    <DialogRoot open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
      <ShortcutTooltip
        label="Customize columns"
        shortcutSequence={showShortcut ? VIEW_SHORTCUTS.openColumnChooser.sequence : undefined}
        disabled={!isV2}
      >
        <DialogTrigger asChild>
          <Button variant="neutral" size="sm" data-testid={triggerTestId}>
            <Columns />
            {triggerLabel
              ? `${triggerLabel}: ${displayedColumnIds.length}`
              : displayedColumnIds.length}
          </Button>
        </DialogTrigger>
      </ShortcutTooltip>

      {isDialogOpen && (
        <ChooserBody
          columns={columns}
          selectedIds={localDisplayedColumnIds}
          searchText={searchText}
          onSearchTextChange={setSearchText}
          onSearchBlur={(text) => logSearch(text, "Blur")}
          userExpandedGroups={userExpandedGroups}
          onExpandedGroupsChange={setUserExpandedGroups}
          getGroupIcon={getGroupIcon}
          title={title}
          showResetToDefault={defaultColumnIds !== undefined}
          onToggle={handleColumnToggle}
          onGroupToggle={handleGroupToggle}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          onResetToDefault={handleResetToDefault}
          onSettingsLinkClick={() => close(false)}
          focusSearchOnOpen={isV2}
        />
      )}
    </DialogRoot>
  )
}
